import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

const LabBookingContext = createContext(null);

// sessionStorage helpers so a page refresh doesn't kill mid-flow state.
const ss = {
  get: (key, fallback = null) => {
    try { return JSON.parse(sessionStorage.getItem(key)) ?? fallback; }
    catch { return fallback; }
  },
  set: (key, val) => { try { sessionStorage.setItem(key, JSON.stringify(val)); } catch {} },
  del: (key) => { try { sessionStorage.removeItem(key); } catch {} },
};

export function LabBookingProvider({ children }) {
  const [selectedTests, setSelectedTests] = useState(() => ss.get("lb_tests", []));
  const [selectedLab, setSelectedLab]     = useState(() => ss.get("lb_lab"));
  const [patient, setPatient]             = useState(() => ss.get("lb_patient"));
  const [collectionType, setCollectionType] = useState(
    () => sessionStorage.getItem("lb_collection_type") || "home"
  );

  // Lab that the user tried to book before being redirected to login.
  // Shape: { lab, testIds (string), collectionType }.
  // Stored in sessionStorage so a login-page refresh doesn't lose it.
  const [pendingLabBooking, setPendingLabBooking] = useState(
    () => ss.get("lb_pending")
  );

  // Sync all transient state to sessionStorage on change.
  useEffect(() => { ss.set("lb_tests", selectedTests); }, [selectedTests]);
  useEffect(() => { ss.set("lb_lab",   selectedLab);   }, [selectedLab]);
  useEffect(() => { ss.set("lb_patient", patient);     }, [patient]);
  useEffect(() => { sessionStorage.setItem("lb_collection_type", collectionType); }, [collectionType]);
  useEffect(() => { ss.set("lb_pending", pendingLabBooking); }, [pendingLabBooking]);

  // Auth — token lives in localStorage (read by lib/api.js interceptor).
  const [user, setUser] = useState(() =>
    JSON.parse(localStorage.getItem("lab_booking_user") || "null")
  );

  const login = useCallback((userData) => {
    localStorage.setItem("lab_booking_token", userData.access_token);
    localStorage.setItem("lab_booking_user", JSON.stringify(userData));
    setUser(userData);
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem("lab_booking_token");
    localStorage.removeItem("lab_booking_user");
    setUser(null);
  }, []);

  const deviceId = useMemo(() => {
    let id = localStorage.getItem("lab_booking_device_id");
    if (!id) {
      id = "web-" + Math.random().toString(36).slice(2) + Date.now();
      localStorage.setItem("lab_booking_device_id", id);
    }
    return id;
  }, []);

  const [completedOrder, setCompletedOrder] = useState(null);

  // 'idle' | 'loading' | 'granted' | 'denied' | 'unsupported'
  const [locationStatus, setLocationStatus] = useState("idle");
  const [location, setLocation] = useState(() =>
    JSON.parse(localStorage.getItem("lab_booking_location") || "null")
  );

  const saveLocation = useCallback((loc) => {
    if (loc == null) {
      localStorage.removeItem("lab_booking_location");
      setLocation(null);
      setLocationStatus("idle");
    } else {
      localStorage.setItem("lab_booking_location", JSON.stringify(loc));
      setLocation(loc);
      setLocationStatus("granted");
    }
  }, []);

  // requestLocation — used by pages that auto-trigger GPS on mount.
  // Guard: no-op if location already set or GPS already in-flight, so
  // the CompareLabsPage useEffect doesn't loop when requestLocation
  // reference changes after a successful GPS fix.
  const requestLocation = useCallback(() => {
    if (location || locationStatus === "loading") return;
    if (!("geolocation" in navigator)) {
      setLocationStatus("unsupported");
      return;
    }
    setLocationStatus("loading");
    navigator.geolocation.getCurrentPosition(
      (position) => {
        saveLocation({ latitude: position.coords.latitude, longitude: position.coords.longitude });
      },
      () => setLocationStatus("denied"),
      { enableHighAccuracy: false, timeout: 30000 }
    );
  }, [location, locationStatus, saveLocation]);

  // gpsLocation — used by the explicit "Use my location" button in the
  // location modal. Always proceeds regardless of whether location is set,
  // so the user can refresh their GPS location on demand.
  const gpsLocation = useCallback(() => {
    if (locationStatus === "loading") return;
    if (!("geolocation" in navigator)) {
      setLocationStatus("unsupported");
      return;
    }
    setLocationStatus("loading");
    navigator.geolocation.getCurrentPosition(
      (position) => {
        saveLocation({ latitude: position.coords.latitude, longitude: position.coords.longitude });
      },
      () => setLocationStatus("denied"),
      { enableHighAccuracy: false, timeout: 30000 }
    );
  }, [locationStatus, saveLocation]);

  const selectionKey = (item) => {
    if (item.is_package)  return `package:${item.name}`;
    if (item.is_unmapped) return `unmapped:${item.name}`;
    return `test:${item.test_master_id}`;
  };

  const addTest = useCallback((item) => {
    const key = selectionKey(item);
    setSelectedTests((prev) =>
      prev.some((t) => selectionKey(t) === key) ? prev : [...prev, item]
    );
  }, []);

  const removeTest = useCallback((item) => {
    const key = selectionKey(item);
    setSelectedTests((prev) => prev.filter((t) => selectionKey(t) !== key));
  }, []);

  const isTestSelected = useCallback(
    (item) => selectedTests.some((t) => selectionKey(t) === selectionKey(item)),
    [selectedTests]
  );

  // Clears all booking session state (called after payment succeeds).
  const clearSelection = useCallback(() => {
    setSelectedTests([]);
    setSelectedLab(null);
    setPatient(null);
    setCollectionType("home");
    setPendingLabBooking(null);
    ["lb_tests", "lb_lab", "lb_patient", "lb_collection_type", "lb_pending"].forEach(ss.del);
  }, []);

  const value = useMemo(
    () => ({
      selectedTests,
      addTest,
      removeTest,
      isTestSelected,
      clearSelection,
      selectedLab,
      setSelectedLab,
      pendingLabBooking,
      setPendingLabBooking,
      user,
      login,
      logout,
      deviceId,
      patient,
      setPatient,
      collectionType,
      setCollectionType,
      completedOrder,
      setCompletedOrder,
      location,
      locationStatus,
      requestLocation,
      gpsLocation,
      saveLocation,
    }),
    [
      selectedTests,
      addTest,
      removeTest,
      isTestSelected,
      clearSelection,
      selectedLab,
      pendingLabBooking,
      user,
      login,
      logout,
      deviceId,
      patient,
      collectionType,
      completedOrder,
      location,
      locationStatus,
      requestLocation,
      gpsLocation,
      saveLocation,
    ]
  );

  return (
    <LabBookingContext.Provider value={value}>
      {children}
    </LabBookingContext.Provider>
  );
}

export function useLabBooking() {
  const ctx = useContext(LabBookingContext);
  if (!ctx) throw new Error("useLabBooking must be used within a LabBookingProvider");
  return ctx;
}
